import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "traeva_prompt_state"; // "dismissed" | "engaged"
const SCROLL_THRESHOLD = 0.8;

// CTAs that count as "engagement" — visitor has shown intent, don't nag.
const ENGAGED_TESTIDS = new Set([
  "nav-cta-button",
  "nav-cta-button-mobile",
  "hero-primary-cta",
  "cta-primary",
  "signal-cta",
  "signal-final-cta",
  "signal-prompt-cta",
  "how-it-works-cta",
  "about-cta",
  "services-cta-link",
  "footer-cta-link",
  "case-studies-cta-link",
  "contact-submit",
]);

export default function SignalPrompt() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = sessionStorage.getItem(STORAGE_KEY);
    return v === "dismissed" || v === "engaged";
  });

  const onContact = pathname === "/contact";

  const markEngaged = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, "engaged");
    setClosed(true);
    setVisible(false);
  }, []);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, "dismissed");
    setClosed(true);
    setVisible(false);
  }, []);

  // Delegated click listener to detect engagement with any qualifying CTA.
  useEffect(() => {
    if (closed) return;
    const handler = (e) => {
      const el = e.target.closest("[data-testid]");
      if (!el) return;
      const id = el.getAttribute("data-testid");
      if (ENGAGED_TESTIDS.has(id)) markEngaged();
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [closed, markEngaged]);

  // Scroll watcher — show prompt past 80% on non-contact pages.
  useEffect(() => {
    if (closed || onContact) {
      setVisible(false);
      return;
    }
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const ratio = window.scrollY / scrollable;
      if (ratio >= SCROLL_THRESHOLD) setVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [closed, onContact, pathname]);

  // Hide instantly if visitor lands on /contact
  useEffect(() => {
    if (onContact) setVisible(false);
  }, [onContact]);

  return (
    <AnimatePresence>
      {visible && !closed && !onContact && (
        <motion.div
          key="signal-prompt"
          data-testid="signal-prompt-bar"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[60] bg-ink text-white"
          style={{ minHeight: "64px" }}
        >
          <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 h-full flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-6 py-3 sm:py-0 sm:h-[56px] text-center sm:text-left">
            <p
              className="text-[13px] font-light leading-snug"
              style={{ color: "rgba(255,255,255,0.6)" }}
            >
              Not sure if Traeva is right for your brand?
            </p>

            <div className="flex items-center gap-5 sm:gap-8">
              <Link
                to="/bearing"
                data-testid="signal-prompt-cta"
                onClick={markEngaged}
                className="text-[13px] font-medium text-coral hover:text-coral-soft transition-colors whitespace-nowrap"
              >
                Request a Bearing — no commitment →
              </Link>
              <button
                type="button"
                aria-label="Close"
                data-testid="signal-prompt-close"
                onClick={dismiss}
                className="hidden sm:inline-flex items-center justify-center transition-colors"
                style={{ color: "rgba(255,255,255,0.3)" }}
              >
                <X size={10} strokeWidth={2} />
              </button>
            </div>

            {/* Mobile close, top-right corner */}
            <button
              type="button"
              aria-label="Close"
              data-testid="signal-prompt-close-mobile"
              onClick={dismiss}
              className="sm:hidden absolute top-2 right-3 transition-colors"
              style={{ color: "rgba(255,255,255,0.3)" }}
            >
              <X size={12} strokeWidth={2} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
